/** Métricas derivadas de uma atividade (Fase 4): parciais por km, melhores esforços, zonas de FC, intensidade e RPE. */
import { useState } from "react";
import { api, ApiError } from "../api/client";
import type { ActivityDetail, ActivityMetrics } from "../api/types";
import { formatDuration, formatPace, paceShort } from "../lib/format";
import { HBars, INTENSITY_LABEL } from "./charts";
import { Badge, useToast } from "./ui";
import { LOCALE, t, tj } from "../i18n";

const BASIS_LABEL: Record<string, string> = {
  hr: t("pela frequência cardíaca"),
  rpe: t("pelo esforço percebido (RPE)"),
  workout_type: t("pelo tipo de treino prescrito"),
};
const ZONE_BASIS: Record<string, string> = {
  profile_zones: t("zonas definidas no perfil do atleta"),
  hr_max_profile: t("faixas de % da FC máxima do perfil"),
};

export function RpePicker({ value, onChange, disabled }: { value: number | null; onChange: (v: number | null) => void; disabled?: boolean }) {
  return (
    <div>
      <div className="scale" role="group" aria-label={t("Esforço percebido de 1 a 10")}>
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button key={n} type="button" aria-pressed={value === n} disabled={disabled} onClick={() => onChange(value === n ? null : n)}>
            {n}
          </button>
        ))}
      </div>
      <div className="scale-hint" style={{ maxWidth: 460 }}>
        <span>{t("1 muito leve")}</span>
        <span>{t("5 moderado")}</span>
        <span>{t("10 máximo")}</span>
      </div>
    </div>
  );
}

export function RpePanel({ a, path, canRate, onSaved }: { a: ActivityDetail; path: string; canRate: boolean; onSaved: (a: ActivityDetail) => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const fromWorkout = a.comparison?.planned_workout.rpe ?? null;
  async function save(v: number | null) {
    setBusy(true);
    try {
      onSaved(await api.patch<ActivityDetail>(path, { rpe: v }));
      toast(v ? t("Esforço {v} registrado.", { v }) : t("Esforço removido."));
    } catch (e) {
      toast((e as ApiError).message, "error");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel section" aria-labelledby="rpe">
      <div className="panel-head">
        <h2 id="rpe">{t("Esforço percebido")}</h2>
      </div>
      <div className="panel-body stack">
        {fromWorkout ? (
          <p>
            {tj("{rpe} registrado no treino vinculado. É esse valor que entra na carga de treino.", { rpe: <strong>RPE {fromWorkout}</strong> })}
          </p>
        ) : canRate ? (
          <>
            <p className="muted">{t("Quão puxado foi? A nota (escala CR-10) multiplica a duração e vira a carga de treino (sRPE).")}</p>
            <RpePicker value={a.rpe} onChange={save} disabled={busy} />
          </>
        ) : (
          <p>{a.effective_rpe ? <strong>RPE {a.effective_rpe}</strong> : t("O atleta ainda não informou o esforço desta atividade.")}</p>
        )}
      </div>
    </section>
  );
}

export function ActivityMetricsView({ m }: { m: ActivityMetrics }) {
  const splits = m.splits;
  const fastest = Math.min(...splits.filter((s) => !s.partial && s.pace_s_per_km).map((s) => s.pace_s_per_km!));
  const slowest = Math.max(...splits.filter((s) => s.pace_s_per_km).map((s) => s.pace_s_per_km!));
  const quality: string[] = [];
  if (m.data_quality.has_stream === false) quality.push(t("O arquivo não traz séries ponto a ponto: parciais e melhores esforços não são calculados."));
  if (m.data_quality.distance_consistent === false)
    quality.push(t("A distância das séries difere mais de 5 % do total do arquivo: parciais e melhores esforços foram omitidos."));
  if (m.data_quality.hr_max_known === false) quality.push(t("Sem FC máxima no perfil: zonas e intensidade por FC não são calculadas."));
  return (
    <section className="section" aria-labelledby="metricas">
      <div className="row-between">
        <h2 id="metricas">{t("Métricas da atividade")}</h2>
        <span className="muted" style={{ fontSize: "var(--step--1)" }}>
          {" "}{t("Cálculo {algorithm_version}", { algorithm_version: m.algorithm_version })}
        </span>
      </div>
      {quality.length > 0 && (
        <ul className="muted" style={{ fontSize: "var(--step--1)", margin: "6px 0 12px", paddingLeft: 18 }}>
          {quality.map((q) => (
            <li key={q}>{q}</li>
          ))}
        </ul>
      )}
      <div className="chart-grid" style={{ marginTop: 10 }}>
        {splits.length > 0 && (
          <div className="panel">
            <div className="panel-head">
              <h3>{t("Parciais por quilômetro")}</h3>
            </div>
            <div className="table-wrap">
              <table className="data splits-table">
                <thead>
                  <tr>
                    <th>{t("Km")}</th>
                    <th>{t("Ritmo")}</th>
                    <th className="bar-cell">
                      <span className="sr-only">{t("Comparação")}</span>
                    </th>
                    <th>{t("FC")}</th>
                    <th>{t("Desnível")}</th>
                  </tr>
                </thead>
                <tbody>
                  {splits.map((s) => {
                    const p = s.pace_s_per_km;
                    // barra maior = mais rápido (o atleta lê "mais barra, mais rápido")
                    const w = p && slowest > fastest ? 25 + ((slowest - p) / (slowest - fastest)) * 75 : 60;
                    return (
                      <tr key={s.index}>
                        <td className="num">
                          {s.partial ? `${(s.distance_m / 1000).toLocaleString(LOCALE, { maximumFractionDigits: 2 })}` : s.index}
                          {s.partial && <span className="sub"> {t("parcial")}</span>}
                        </td>
                        <td className="num">
                          <strong>{p ? paceShort(p) : "—"}</strong>
                          {p === fastest && !s.partial && <span className="sub"> {t("mais rápido")}</span>}
                        </td>
                        <td className="bar-cell" aria-hidden>
                          <span className="split-bar" style={{ width: `${w}%` }} />
                        </td>
                        <td className="num">{s.avg_hr ?? "—"}</td>
                        <td className="num nowrap">
                          {s.elev_gain_m != null ? `+${s.elev_gain_m} −${s.elev_loss_m ?? 0} m` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="stack">
          <div className="panel">
            <div className="panel-head">
              <h3>{t("Intensidade")}</h3>
            </div>
            <div className="panel-body stack">
              {m.intensity ? (
                <p>
                  <Badge tone={m.intensity === "high" ? "danger" : m.intensity === "moderate" ? "warn" : "ok"}>{INTENSITY_LABEL[m.intensity]}</Badge>{" "}
                  <span className="muted">{BASIS_LABEL[m.intensity_basis ?? ""]}</span>
                </p>
              ) : (
                <p className="muted">{t("Sem dados para classificar (FC com faixas ou FC máxima no perfil, RPE ou treino prescrito).")}</p>
              )}
              {m.data_quality.intensity_basis && (
                <p className="muted" style={{ fontSize: "var(--step--1)" }}>
                  {m.data_quality.intensity_basis === "declared_bands" ? t("Faixas de FC: declaradas no perfil do atleta.") : t("Faixas de FC: 82% e 88% da FC máxima do perfil.")}
                </p>
              )}
              {m.hr_zones && m.hr_zones.some((z) => z.seconds > 0) && (
                <>
                  <HBars
                    rows={m.hr_zones.map((z) => ({ key: z.name, label: z.name, sub: `${z.min}–${z.max >= 250 ? "" : z.max} bpm`, value: z.seconds }))}
                    fmt={(v) => formatDuration(v)}
                  />
                  <p className="muted" style={{ fontSize: "var(--step--1)" }}>
                    {t("Tempo em cada zona de FC — {hr_zone_basis}.", { hr_zone_basis: ZONE_BASIS[m.hr_zone_basis ?? ""] })}</p>
                </>
              )}
            </div>
          </div>
          {m.best_efforts.length > 0 && (
            <div className="panel">
              <div className="panel-head">
                <h3>{t("Melhores esforços nesta atividade")}</h3>
              </div>
              <ul className="list-plain">
                {m.best_efforts.map((e) => (
                  <li key={e.distance_key} className="row-between">
                    <span>{e.label}</span>
                    <span className="figs">
                      <strong>{formatDuration(e.time_s)}</strong> <span className="muted">{formatPace(e.pace_s_per_km)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(m.max_speed_mps || m.max_cadence_spm || m.elevation_loss_m != null) && (
            <dl className="facts">
              {m.max_speed_mps && (
                <>
                  <dt>{t("Velocidade máxima")}</dt>
                  <dd className="num">
                    {t("{v} km/h ({pace}/km)", { v: (m.max_speed_mps * 3.6).toLocaleString(LOCALE, { maximumFractionDigits: 1 }), pace: paceShort(1000 / m.max_speed_mps) })}
                  </dd>
                </>
              )}
              {m.max_cadence_spm && (
                <>
                  <dt>{t("Cadência máxima")}</dt>
                  <dd className="num">{t("{v} passos/min", { v: m.max_cadence_spm })}</dd>
                </>
              )}
              {m.elevation_loss_m != null && (
                <>
                  <dt>{t("Perda de elevação")}</dt>
                  <dd className="num">{m.elevation_loss_m} m</dd>
                </>
              )}
            </dl>
          )}
        </div>
      </div>
    </section>
  );
}
